/**
 * Stale-chunk recovery after a frontend deploy.
 *
 * Each deploy rebuilds the SPA with new content-hashed chunk filenames
 * (e.g. Home-<hash>.js). A browser tab that loaded the app BEFORE the deploy
 * still holds the old entry graph and, when it lazily navigates to a route,
 * requests the now-deleted old chunk filename. nginx returns 404 and the
 * dynamic import rejects with "Failed to fetch dynamically imported module".
 *
 * This is transient: reloading the page fetches the fresh index.html, which
 * references the new hashes. We listen for Vite's preloadError event and
 * vue-router's onError, detect the dynamic-import failure, and reload once.
 * A sessionStorage debounce prevents a reload loop if the chunk is genuinely
 * missing for some other reason (so Sentry still captures the real failure).
 */
import type { Router } from "vue-router";

const RELOAD_GUARD_KEY = "nib_chunk_reload_at";
const RELOAD_DEBOUNCE_MS = 15000;

function isDynamicImportError(message: string): boolean {
  return (
    /Failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message) ||
    /is not a valid JavaScript MIME type/i.test(message)
  );
}

function reloadOnce(targetUrl?: string): void {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) || 0);
    // Reloaded very recently — the chunk is genuinely unavailable, not a stale
    // deploy. Stop reloading and let the error surface to Sentry.
    if (Date.now() - last < RELOAD_DEBOUNCE_MS) return;
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
  } catch {
    // sessionStorage unavailable (private mode) — fall through to a single reload.
  }
  window.location.assign(targetUrl || window.location.href);
}

export function installChunkReloadGuard(router: Router): void {
  // Vite emits this when its preload helper fails to fetch a lazy chunk.
  window.addEventListener("vite:preloadError", (event) => {
    event.preventDefault();
    reloadOnce();
  });

  // vue-router surfaces lazy route-component import failures here.
  router.onError((error, to) => {
    const message = error instanceof Error ? error.message : String(error);
    if (isDynamicImportError(message)) {
      reloadOnce(to?.fullPath);
    }
  });
}
